// ─────────────────────────────────────────────────────────────────────────────
// FILE: src/components/HRComponents/MyLeavesList.jsx
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Plus, Trash2, CalendarDays, X, Eye, FileText, ExternalLink } from "lucide-react";
import { toast } from "react-hot-toast"; 
import SummaryApi from "../../common/SummaryApi";
import api from "../../common/apiClient";
import SelectDropdown from "../../customStyles/SelectDropdown";
import CustomDatePicker from "../../customStyles/CustomDatePicker";
import ModernTable from "../ui/ModernTable";
import PageHeader from "../ui/PageHeader";
import DeleteConfirmationOffcanvas from "../../helpers/DeleteConfirmationOffcanvas"; 
import { formatDateTime } from "../../helpers/formatDateTime";


const MyLeavesList = ({ user }) => {
  // --- State Management ---
  const [leaves, setLeaves] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]); 
  const [isLoading, setIsLoading] = useState(false); 
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false); // View details drawer state

  // Cancellation states
  const [isCancelConfirmOpen, setIsCancelConfirmOpen] = useState(false);
  const [requestToCancel, setRequestToCancel] = useState(null);
  const [cancelReason, setCancelReason] = useState(""); // Captures cancellation remarks

  // Pagination states
  const [currentPage, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  const [compOffGrants, setCompOffGrants] = useState([]);
  // --- Form State (For Applying Leave) ---
  const [formData, setFormData] = useState({
    leaveTypeId: "",
    startDate: "",
    endDate: "",
    isHalfDay: false,
    halfDayType: "first_half",
    reason: "",
    compOffGrantId: "",
  });

  const [files, setFiles] = useState([]); 
  const [formErrors, setFormErrors] = useState({});

  const halfDayOptions = [
    { label: "First Half (Morning Session)", id: "first_half" },
    { label: "Second Half (Afternoon Session)", id: "second_half" },
  ];

  const leaveTypeOptions = leaveTypes.map(type => ({
    label: `${type.name} (${type.code})`,
    id: type._id
  }));

  const fetchLeaveTypes = useCallback(async () => {
    try {
      const url = SummaryApi.getMyLeaveTypes?.url || "/api/hr/leave-types/view-all";
      const method = SummaryApi.getMyLeaveTypes?.method || "get";
      const res = await api({ url, method });
      if (res.data.success) {
        setLeaveTypes(res.data.data || []);
      }
    } catch (err) {
      console.error("Failed to load leave types:", err);
    }
  }, []);

  const fetchMyLeaves = useCallback(async () => {
    setIsLoading(true);
    try {
      const baseUrl = SummaryApi.getAllLeaveRequests?.url || "/api/hr/leaves";
      const method = SummaryApi.getAllLeaveRequests?.method || "get";

      const url = `${baseUrl}?userId=${user._id}&page=${currentPage}&limit=${itemsPerPage}`;
      const res = await api({ url, method });

      if (res.data.success) {
        setLeaves(res.data.data || []);
        setTotalItems(res.data.total || res.data.data.length);
      } else {
        toast.error(res.data.message || "Failed to retrieve leave history.");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Error loading leave applications.");
    } finally {
      setIsLoading(false);
    }
  }, [user._id, currentPage, itemsPerPage]);

  useEffect(() => {
    fetchLeaveTypes();
  }, [fetchLeaveTypes]);

  useEffect(() => {
    fetchMyLeaves();
  }, [fetchMyLeaves]);

  
  const selectedLeaveTypeConfig = useMemo(() => {
    return leaveTypes.find(t => t._id === formData.leaveTypeId);
  }, [formData.leaveTypeId, leaveTypes]);

  // Add this below selectedLeaveTypeConfig in MyLeavesList.jsx
  const requestedDaysEstimate = useMemo(() => {
    if (formData.isHalfDay) return 0.5; // Half-day எனில் 0.5 நாட்கள்
    if (!formData.startDate || !formData.endDate) return 0;

    const start = new Date(formData.startDate);
    const end = new Date(formData.endDate);
    
    if (isNaN(start.getTime()) || isNaN(end.getTime()) || end < start) return 0;

    // ஞாயிற்றுக்கிழமைகளைத் (Sundays) தவிர்த்து வேலை நாட்களை மட்டும் கணக்கிடும் எளிய லாஜிக்
    let daysCount = 0;
    const tempDate = new Date(start);
    while (tempDate <= end) {
      if (tempDate.getDay() !== 0) { // 0 = Sunday (ஞாயிறு தவிர்த்து)
        daysCount++;
      }
      tempDate.setDate(tempDate.getDate() + 1);
    }
    return daysCount;
  }, [formData.startDate, formData.endDate, formData.isHalfDay]);


  // Available Comp-Off Grants-ஐப் பெறுகிறது
  const fetchAvailableCompOffs = useCallback(async () => {
    try {
      const baseUrl = SummaryApi.compOffAvailable?.url ||"/api/hr/compoff/available"; // முந்தைய பகுதியில் உருவாக்கிய API
      const res = await api.get(baseUrl);
      if (res.data.success) {
        setCompOffGrants(res.data.data || []);
      }
    } catch (err) {
      console.error("Failed to load available comp-off grants:", err);
    }
  }, []);

  // லீவ் டைப் 'CO' ஆக இருந்தால் மட்டும் தானாக லோடு செய்யும்
  useEffect(() => {
    const isCompOff = selectedLeaveTypeConfig?.code === "CO" || selectedLeaveTypeConfig?.code === "COMP_OFF";
    if (isCompOff) {
      fetchAvailableCompOffs();
    } else {
      setCompOffGrants([]);
      setFormData(prev => ({ ...prev, compOffGrantId: "" }));
    }
  }, [selectedLeaveTypeConfig, fetchAvailableCompOffs]);

  // --- Form Helper Actions ---
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handleSelectChange = (field, val) => {
    setFormData(prev => ({ ...prev, [field]: val }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(selectedFiles);
    if (formErrors.files) {
      setFormErrors(prev => ({ ...prev, files: "" }));
    }
  };

  const openFormDrawer = () => {
    setFormData({
      leaveTypeId: "",
      startDate: "",
      endDate: "",
      isHalfDay: false,
      halfDayType: "first_half",
      reason: "",
      compOffGrantId: "",
    });
    setFiles([]);
    setFormErrors({});
    setIsFormOpen(true);
  };

  const closeFormDrawer = () => {
    setIsFormOpen(false);
  };

  const openViewDrawer = (request) => {
    setRequestToCancel(request); // Reusing tracking state variable for reading details
    setIsViewOpen(true);
  };

  const closeViewDrawer = () => {
    setIsViewOpen(false);
    setRequestToCancel(null);
  };

  const validateForm = () => {
      const errors = {};
      if (!formData.leaveTypeId) errors.leaveTypeId = "Please select a leave type";
      if (!formData.startDate) errors.startDate = "Start date is required";
      if (!formData.isHalfDay && !formData.endDate) errors.endDate = "End date is required";
      if (!formData.reason.trim()) errors.reason = "Please specify a reason for leave";

      // ✅ Comp-off லீவ் எனில், வேலை செய்த நாளைத் தேர்ந்தெடுப்பது கட்டாயம் [1]
      const isCompOff = selectedLeaveTypeConfig?.code === "CO" || selectedLeaveTypeConfig?.code === "COMP_OFF";
      if (isCompOff && !formData.compOffGrantId) {
        errors.compOffGrantId = "Please select a worked Holiday/Sunday to claim this Comp-off";
      }

      if (selectedLeaveTypeConfig?.requiresAttachment) {
        const start = new Date(formData.startDate);
        const end = formData.isHalfDay ? start : new Date(formData.endDate);
        const diffTime = Math.abs(end - start);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;

        if (diffDays >= selectedLeaveTypeConfig.attachmentThresholdDays && files.length === 0) {
          errors.files = `An attachment is required for ${selectedLeaveTypeConfig.name} of ${selectedLeaveTypeConfig.attachmentThresholdDays} or more days.`;
        }
      }

      setFormErrors(errors);
      return Object.keys(errors).length === 0;
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    
    const submitData = new FormData();
    submitData.append("leaveTypeId", formData.leaveTypeId);
    submitData.append("startDate", formData.startDate);
    submitData.append("endDate", formData.isHalfDay ? formData.startDate : formData.endDate);
    submitData.append("isHalfDay", formData.isHalfDay);
    if (formData.isHalfDay) {
      submitData.append("halfDayType", formData.halfDayType);
    }
    submitData.append("reason", formData.reason);

    files.forEach((file) => {
      submitData.append("documents", file);
    });
    if (formData.compOffGrantId) {
          submitData.append("compOffGrantId", formData.compOffGrantId);
    }
    try {
      const url = SummaryApi.applyLeave?.url || "/api/hr/leaves";
      const method = SummaryApi.applyLeave?.method || "post";

      const res = await api({
        url,
        method,
        data: submitData,
        headers: { "Content-Type": "multipart/form-data" }, 
      });

      if (res.data.success) {
        toast.success(res.data.message || "Leave applied successfully!");
        await fetchMyLeaves();
        closeFormDrawer();
      } else {
        toast.error(res.data.message || "Failed to submit leave request.");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Error submitting leave request.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const triggerCancelLeave = (request) => {
    setRequestToCancel(request);
    setCancelReason(""); 
    setIsCancelConfirmOpen(true);
  };

  const confirmCancelLeave = async () => {
    if (!requestToCancel) return;
    if (!cancelReason.trim()) {
      toast.error("Please specify a reason for cancellation.");
      return;
    }

    setIsLoading(true);
    try {
      const url = SummaryApi.cancelLeave?.url(requestToCancel._id) || `/api/hr/leaves/${requestToCancel._id}/cancel`;
      const method = SummaryApi.cancelLeave?.method || "put";

      const res = await api({ 
        url, 
        method, 
        data: { cancellationReason: cancelReason } 
      });

      if (res.data.success) {
        toast.success(res.data.message || "Leave request cancelled successfully.");
        await fetchMyLeaves();
        setIsCancelConfirmOpen(false);
        setRequestToCancel(null);
        setCancelReason("");
      } else {
        toast.error(res.data.message || "Failed to cancel request.");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Error cancelling leave request.");
    } finally {
      setIsLoading(false);
    }
  };

  // --- Table Columns Mapping ---
  const columns = [
    {
      key: "leaveTypeId",
      label: "Leave Type",
      render: (row) => (
        <span
          className="px-2.5 py-1 rounded text-xs font-bold uppercase tracking-wider"
          style={{
            backgroundColor: `${row.leaveTypeId?.color || "#6366f1"}15`,
            color: row.leaveTypeId?.color || "#6366f1",
            border: `1px solid ${row.leaveTypeId?.color || "#6366f1"}30`,
          }}
        >
          {row.leaveTypeId?.code || "LV"}
        </span>
      ),
    },
    {
      key: "duration",
      label: "Duration / Period",
      render: (row) => {
        const start = formatDateTime(row.startDate, false);
        const end = formatDateTime(row.endDate, false);
        return (
          <span className="font-semibold text-gray-700">
            {row.isHalfDay ? `${start} (Half-Day)` : `${start} - ${end}`}
          </span>
        );
      },
    },
    {
      key: "totalDays",
      label: "Total Days",
      render: (row) => <span className="font-bold text-gray-800">{row.totalDays} Day(s)</span>,
    },
    {
      key: "reason",
      label: "Reason",
      render: (row) => (
        <span className="text-gray-500 text-xs truncate max-w-xs block font-medium" title={row.reason}>
          {row.reason || "Not specified"}
        </span>
      ),
    },
    {
      key: "status",
      label: "Status",
      render: (row) => {
        const colors = {
          pending: "bg-yellow-50 text-yellow-700 border-yellow-200",
          approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
          rejected: "bg-red-50 text-red-700 border-red-200",
          cancelled: "bg-gray-100 text-gray-500 border-gray-200"
        };
        return (
          <span className={`px-2.5 py-0.5 rounded border text-xs font-bold uppercase tracking-wider ${colors[row.status]}`}>
            {row.status}
          </span>
        );
      },
    },
  ];

  const tableActions = [
    {
      label: "View Application Details",
      icon: <Eye className="w-4 h-4 text-blue-600" />,
      onClick: (row) => openViewDrawer(row),
    },
    {
      label: "Cancel Leave Request",
      icon: <Trash2 className="w-4 h-4 text-red-500" />,
      onClick: (row) => triggerCancelLeave(row), 
      renderCondition: (row) => row.status === "pending", // ✅ FIXED: Restricted cancel button ONLY to pending requests [1]
    },
  ];

  return (
    <div className="p-4">
      <PageHeader
        title="My Leave Requests"
        description="Submit leave applications, track pending approvals, and manage your overall leave timeline."
        titleIcon={CalendarDays}
        action={{
          label: "Apply for Leave",
          icon: <Plus className="w-4 h-4" />,
          onClick: openFormDrawer,
        }}
      />

      <div className="mt-6 relative overflow-hidden">
        {isLoading && (
          <div className="absolute inset-0 bg-white/60 backdrop-blur-[1px] flex items-center justify-center z-10 transition-all rounded-2xl">
            <span className="text-xs font-bold text-gray-500 animate-pulse tracking-wide">Refreshing leave history...</span>
          </div>
        )}
        <ModernTable
          columns={columns}
          data={leaves}
          actions={tableActions}
          selectable={false}
          actionType="dropdown"
          pagination={{
            totalItems,
            itemsPerPage,
            currentPage,
            onPageChange: (page) => setPage(page),
            onItemsPerPageChange: (limit) => {
              setItemsPerPage(limit);
              setPage(1);
            }
          }}
        />
      </div>

      {/* ─────────────────────────────────────────────────────────────────── */}
      {/* RIGHT-SIDE VIEW DETAILS DRAWER (View One) */}
      {/* ─────────────────────────────────────────────────────────────────── */}
      <div
        className={`fixed inset-0 top-[57px] md:top-[65px] z-[65] overflow-hidden transition-all duration-300 ${
          isViewOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={closeViewDrawer} />
        
        <div className="absolute inset-y-0 right-0 max-w-full flex">
          <div
            className={`w-screen max-w-md bg-white shadow-2xl flex flex-col transform transition-transform duration-300 ease-in-out ${
              isViewOpen ? "translate-x-0" : "translate-x-full"
            }`}
          >
            {/* Drawer Header */}
            <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between bg-slate-50">
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  Leave Request Details
                </h3>
                <p className="text-xs text-gray-400 mt-0.5">Read-only overview of your leave application.</p>
              </div>
              <button
                type="button"
                onClick={closeViewDrawer}
                className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Read-only Content Body */}
            <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
              {/* Profile Card */}
              <div className="bg-slate-50 p-4 rounded-2xl border border-gray-100 space-y-3">
                <div className="flex items-center gap-3">
                  <span 
                    className="px-3 py-1.5 rounded-lg text-sm font-bold uppercase tracking-wider"
                    style={{
                      backgroundColor: `${requestToCancel?.leaveTypeId?.color || "#6366f1"}15`,
                      color: requestToCancel?.leaveTypeId?.color || "#6366f1",
                      border: `1px solid ${requestToCancel?.leaveTypeId?.color || "#6366f1"}30`
                    }}
                  >
                    {requestToCancel?.leaveTypeId?.code || "LV"}
                  </span>
                  <div className="text-lg font-bold text-gray-800">{requestToCancel?.leaveTypeId?.name}</div>
                </div>

                <div className="pt-2 flex gap-2 flex-wrap">
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${requestToCancel?.leaveTypeId?.isPaid ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
                    {requestToCancel?.leaveTypeId?.isPaid ? "Paid Leave" : "Unpaid Leave"}
                  </span>
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded
                    ${requestToCancel?.status === "pending" ? "bg-yellow-50 text-yellow-700 border-yellow-200" : ""}
                    ${requestToCancel?.status === "approved" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : ""}
                    ${requestToCancel?.status === "rejected" ? "bg-red-50 text-red-700 border-red-200" : ""}
                    ${requestToCancel?.status === "cancelled" ? "bg-gray-100 text-gray-500 border-gray-200" : ""}
                  `}>
                    Status: {requestToCancel?.status}
                  </span>
                </div>
              </div>

              {/* SECTION: Duration & Parameters */}
              <div className="space-y-4">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Leave Parameters</h4>
                <div className="p-4 bg-white border border-gray-200 rounded-2xl shadow-sm space-y-3 text-sm">
                  <div className="flex justify-between items-center pb-2.5 border-b border-gray-50">
                    <span className="text-gray-500 font-medium">Applied Period:</span>
                    <span className="font-bold text-gray-800">
                      {requestToCancel?.isHalfDay 
                        ? `${formatDateTime(requestToCancel.startDate, false)} (Half-Day)`
                        : `${formatDateTime(requestToCancel?.startDate, false)} - ${formatDateTime(requestToCancel?.endDate, false)}`
                      }
                    </span>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-gray-500 font-medium">Total Days:</span>
                    <span className="font-bold text-gray-800">{requestToCancel?.totalDays} Day(s)</span>
                  </div>
                </div>
              </div>

              {/* SECTION: Employee Reason */}
              <div className="space-y-2">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">My Reason</h4>
                <div className="p-3.5 bg-slate-50/50 rounded-xl border border-gray-100 text-sm text-gray-700 leading-relaxed font-medium">
                  "{requestToCancel?.reason || "No reasoning specified"}"
                </div>
              </div>

              {/* SECTION: Uploaded Documents */}
              <div className="space-y-3">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Attached Documents</h4>
                {(requestToCancel?.attachments || []).length === 0 ? (
                  <div className="text-xs text-gray-400 italic py-2">No files or certificate proofs attached to this application.</div>
                ) : (
                  <div className="space-y-2">
                    {(requestToCancel?.attachments || []).map((file, idx) => (
                      <a 
                        key={idx} 
                        href={file.fileUrl} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="flex items-center justify-between p-3 bg-blue-50/50 hover:bg-blue-50 border border-blue-100 rounded-xl text-blue-700 text-xs font-semibold transition-all"
                      >
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4" />
                          <span className="truncate max-w-[220px]">{file.fileName || "View Certificate Document"}</span>
                        </div>
                        <ExternalLink className="w-4 h-4 shrink-0" />
                      </a>
                    ))}
                  </div>
                )}
              </div>

              {/* SECTION: Workflow Status Comments */}
              {requestToCancel?.status !== "pending" && (
                <div className="space-y-3 border-t border-gray-100 pt-5">
                  <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Workflow Logs</h4>
                  
                  {/* Cancelled by Employee logs */}
                  {requestToCancel?.status === "cancelled" && (
                    <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl text-sm">
                      <div className="flex justify-between items-center font-bold text-gray-700">
                        <span>Cancelled by Employee</span>
                        <span className="text-xs font-semibold text-gray-400">
                          {requestToCancel.cancelledAt ? formatDateTime(requestToCancel.cancelledAt, true) : ""}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-2 leading-relaxed">
                        <strong>Reason:</strong> {requestToCancel.approverNote || "No cancellation comments specified"}
                      </p>
                    </div>
                  )}

                  {/* Approved logs */}
                  {requestToCancel?.status === "approved" && (
                    <div className="p-4 bg-emerald-50 border border-emerald-100 rounded-xl text-sm">
                      <div className="flex justify-between items-center font-bold text-emerald-800">
                        <span>
                          Approved by:{" "}
                          {requestToCancel?.approverId
                            ? `${requestToCancel.approverId.firstName} ${requestToCancel.approverId.lastName}`
                            : ""}
                        </span>
                        <span className="text-xs font-semibold text-emerald-600">
                          {requestToCancel.approvedAt ? formatDateTime(requestToCancel.approvedAt, false) : ""}
                        </span>
                      </div>
                      <p className="text-xs text-emerald-700 mt-2 leading-relaxed">
                        <strong>Approver remarks:</strong> {requestToCancel.approverNote || "No manager remarks provided"}
                      </p>
                    </div>
                  )}

                  {/* Rejected logs */}
                  {requestToCancel?.status === "rejected" && (
                    <div className="p-4 bg-red-50 border border-red-100 rounded-xl text-sm">
                      <div className="flex justify-between items-center font-bold text-red-800">
                        <span>Rejected by Manager</span>
                        <span className="text-xs font-semibold text-red-600">
                          {requestToCancel.rejectedAt ? formatDateTime(requestToCancel.rejectedAt, false) : ""}
                        </span>
                      </div>
                      <p className="text-xs text-red-700 mt-2 leading-relaxed">
                        <strong>Rejection reason:</strong> {requestToCancel.approverNote || "No explanation provided"}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Drawer Footer */}
            <div className="px-6 py-4 border-t border-gray-100 bg-slate-50 flex items-center justify-end shrink-0">
              <button
                type="button"
                onClick={closeViewDrawer}
                className="px-5 py-2.5 text-white bg-primary text-sm font-bold rounded-lg transition-colors hover:bg-secondary"
              >
                Close View
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────────────── */}
      {/* RIGHT-SIDE APPLY LEAVE DRAWER (Off-canvas) */}
      {/* ─────────────────────────────────────────────────────────────────── */}
      <div
        className={`fixed inset-0 top-[57px] md:top-[65px] z-[65] overflow-hidden transition-all duration-300 ${
          isFormOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={closeFormDrawer} />
        
        <div className="absolute inset-y-0 right-0 max-w-full flex">
          <div
            className={`w-screen max-w-md bg-white shadow-2xl flex flex-col transform transition-transform duration-300 ease-in-out ${
              isFormOpen ? "translate-x-0" : "translate-x-full"
            }`}
          >
            {/* Drawer Header */}
            <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between bg-slate-50">
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  Apply for Leave
                </h3>
                <p className="text-xs text-gray-400 mt-0.5">Please fill out dates, session types and reasons.</p>
              </div>
              <button
                type="button"
                onClick={closeFormDrawer}
                className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Scrollable Form Body */}
            <form onSubmit={handleFormSubmit} className="flex-1 overflow-y-auto px-6 py-6 space-y-5">
              {/* Select Leave Type Dropdown */}
              <div>
                <SelectDropdown
                  label="Select Leave Type *"
                  options={leaveTypeOptions}
                  value={formData.leaveTypeId}
                  onChange={(val) => handleSelectChange("leaveTypeId", val)}
                  labelKey="label"
                  valueKey="id"
                  placeholder="Choose leave type"
                  zIndex={80}
                />
                {formErrors.leaveTypeId && <p className="text-xs text-red-500 mt-1 font-medium">{formErrors.leaveTypeId}</p>}
              </div>

              {/* ── ✅ மேம்படுத்தப்பட்ட லைவ் லீவ் பேலன்ஸ் கார்டு (Estimated Live Leave Balance Card) ── */}
              {selectedLeaveTypeConfig && (
                <div className="bg-slate-50 border border-gray-100 p-4 rounded-xl space-y-3 mt-2 animate-fadeIn">
                  {/* Row 1: மாதாந்திர ஒதுக்கீடு மற்றும் பயன்பாட்டு விவரங்கள் */}
                  <div className="grid grid-cols-3 gap-2 text-center border-b border-slate-100 pb-3">
                    <div>
                      <span className="text-gray-400 font-bold block uppercase tracking-wider text-[8px]">Allocated Limit</span>
                      <span className="font-bold text-gray-700 text-xs">
                        {(selectedLeaveTypeConfig.allocated || 0) + (selectedLeaveTypeConfig.carriedForward || 0)} d
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-400 font-bold block uppercase tracking-wider text-[8px]">Already Used</span>
                      <span className="font-bold text-gray-700 text-xs">
                        {selectedLeaveTypeConfig.used || 0} d
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-400 font-bold block uppercase tracking-wider text-[8px]">Pending Approval</span>
                      <span className="font-bold text-amber-600 text-xs">
                        {selectedLeaveTypeConfig.pending || 0} d
                      </span>
                    </div>
                  </div>

                  {/* Row 2: தற்போதைய இருப்பு மற்றும் கணக்கிடப்பட்ட புதிய லீவ் இருப்பு */}
                  <div className="space-y-2 text-xs">
                    <div className="flex justify-between items-center text-gray-500 font-semibold">
                      <span>Available Balance:</span>
                      <span className="font-bold text-gray-800">
                        {selectedLeaveTypeConfig.available ?? 0} Day(s)
                      </span>
                    </div>

                    {/* தேதி அல்லது Half-day தேர்ந்தெடுக்கப்பட்டால் மட்டுமே இது காட்டும் */}
                    {requestedDaysEstimate > 0 && (
                      <>
                        <div className="flex justify-between items-center text-gray-500 font-semibold">
                          <span>Requested (This Application):</span>
                          <span className="font-bold text-primary">
                            {requestedDaysEstimate} Day(s)
                          </span>
                        </div>

                        <div className="border-t border-dashed border-gray-200 pt-2.5 flex justify-between items-center font-bold">
                          <span>Projected Balance After Leave:</span>
                          {selectedLeaveTypeConfig.available - requestedDaysEstimate < 0 ? (
                            // இருப்புப் பற்றாக்குறை இருந்தால் சிவப்பு நிறத்தில் எச்சரிக்கும்
                            <span className="text-red-500 font-bold animate-pulse">
                              Insufficient Balance ({selectedLeaveTypeConfig.available - requestedDaysEstimate} Days)
                            </span>
                          ) : (
                            // போதுமான இருப்பு இருந்தால் பச்சை நிறத்தில் காட்டும்
                            <span className="text-emerald-600 font-bold">
                              {selectedLeaveTypeConfig.available - requestedDaysEstimate} Day(s)
                            </span>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
              {/* Select Leave Type Dropdown & Balance card ... */}

              {/* ── ✅ புதிதாகச் சேர்க்கப்பட்ட காம்ப்-ஆஃப் டிராப்டவுன் (Comp-off Claim Dropdown) ── */}
              {(selectedLeaveTypeConfig?.code === "CO" || selectedLeaveTypeConfig?.code === "COMP_OFF") && (
                <div className="animate-fadeIn mt-3">
                  <SelectDropdown
                    label="Claim Worked Sunday / Holiday *"
                    options={compOffGrants.map((g) => ({
                      label: `Worked on ${formatDateTime(g.workedDate, false)} - ${g.reason}`,
                      id: g._id,
                    }))}
                    value={formData.compOffGrantId}
                    onChange={(val) => handleSelectChange("compOffGrantId", val)}
                    labelKey="label"
                    valueKey="id"
                    placeholder="Select worked date to claim"
                    zIndex={75}
                  />
                  {formErrors.compOffGrantId && (
                    <p className="text-xs text-red-500 mt-1 font-medium">{formErrors.compOffGrantId}</p>
                  )}
                </div>
              )}
              <div className="bg-slate-50/50 p-4 rounded-xl border border-gray-100 space-y-4">
                <label className="flex items-center justify-between cursor-pointer">
                  <div>
                    <span className="text-sm font-semibold text-gray-800">Apply for Half-Day</span>
                    <p className="text-[11px] text-gray-400">Request leave for morning or afternoon session only.</p>
                  </div>
                  <input
                    type="checkbox"
                    name="isHalfDay"
                    checked={formData.isHalfDay}
                    onChange={handleInputChange}
                    className="h-4 w-4 accent-red-600 cursor-pointer"
                  />
                </label>

                {formData.isHalfDay && (
                  <div className="pt-3 border-t border-gray-200 animate-fadeIn">
                    <SelectDropdown
                      label="Select Session Type"
                      options={halfDayOptions}
                      value={formData.halfDayType}
                      onChange={(val) => handleSelectChange("halfDayType", val)}
                      labelKey="label"
                      valueKey="id"
                      zIndex={70}
                    />
                  </div>
                )}
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase">
                  {formData.isHalfDay ? "Leave Date *" : "Start Date *"}
                </label>
                <CustomDatePicker
                  value={formData.startDate}
                  onChange={(val) => handleSelectChange("startDate", val)}
                  showCapacityInfo={false}
                />
                {formErrors.startDate && <p className="text-xs text-red-500 mt-1 font-medium">{formErrors.startDate}</p>}
              </div>

              {!formData.isHalfDay && (
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase">End Date *</label>
                  <CustomDatePicker
                    value={formData.endDate}
                    onChange={(val) => handleSelectChange("endDate", val)}
                    showCapacityInfo={false}
                  />
                  {formErrors.endDate && <p className="text-xs text-red-500 mt-1 font-medium">{formErrors.endDate}</p>}
                </div>
              )}

              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase">Reason for Leave *</label>
                <textarea
                  name="reason"
                  rows={4}
                  value={formData.reason}
                  onChange={handleInputChange}
                  placeholder="Describe your reason for leave..."
                  className={`w-full p-3 border rounded-md text-sm focus:outline-none transition-all ${
                    formErrors.reason ? "border-red-500 focus:ring-1 focus:ring-red-200" : "border-gray-200 focus:ring-1 focus:ring-blue-100"
                  }`}
                />
                {formErrors.reason && <p className="text-xs text-red-500 mt-1 font-medium">{formErrors.reason}</p>}
              </div>

              {selectedLeaveTypeConfig?.requiresAttachment && (
                <div className="bg-slate-50/50 p-4 rounded-xl border border-gray-100 space-y-3">
                  <div className="flex items-start gap-1.5">
                    <span className="text-xs font-bold text-gray-700 uppercase">Medical Certificate Required</span>
                  </div>

                  <div className="relative border border-dashed border-gray-200 rounded-lg p-4 bg-white flex flex-col items-center justify-center cursor-pointer hover:bg-slate-50">
                    <input
                      type="file"
                      multiple
                      onChange={handleFileChange}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    />
                    <span className="text-xs text-gray-600 font-semibold">Choose Files to Upload</span>
                    <span className="text-[10px] text-gray-400 mt-0.5">Images, PDFs allowed</span>
                  </div>

                  {files.length > 0 && (
                    <div className="space-y-1.5">
                      <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">Selected Files:</span>
                      {files.map((file, i) => (
                        <div key={i} className="flex items-center justify-between bg-blue-50 border border-blue-100 rounded-lg p-2 text-xs font-semibold text-blue-700">
                          <span className="truncate max-w-[200px]">{file.name}</span>
                          <button
                            type="button"
                            onClick={() => setFiles(prev => prev.filter((_, idx) => idx !== i))}
                            className="text-blue-500 hover:text-blue-700"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {formErrors.files && <p className="text-xs text-red-500 font-medium">{formErrors.files}</p>}
                </div>
              )}
            </form>

            {/* Drawer Footer Buttons */}
            <div className="px-6 py-4 border-t border-gray-100 bg-slate-50 flex items-center justify-end gap-3 shrink-0">
              <button
                type="button"
                onClick={closeFormDrawer}
                className="px-5 py-2.5 border border-gray-200 rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-100 transition-colors"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                onClick={handleFormSubmit}
                disabled={isSubmitting}
                className="px-5 py-2.5 rounded-lg text-sm font-bold text-white transition-colors bg-primary hover:bg-secondary"
              >
                {isSubmitting ? "Applying..." : "Submit Application"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────────────── */}
      {/* ✅ INSTALLED: DELETE CONFIRMATION OFF-CANVAS FOR CANCELLATIONS */}
      {/* ─────────────────────────────────────────────────────────────────── */}
      <DeleteConfirmationOffcanvas
        isOpen={isCancelConfirmOpen}
        onClose={() => {
          setIsCancelConfirmOpen(false);
          setRequestToCancel(null);
          setCancelReason("");
        }}
        onConfirm={confirmCancelLeave}
        title="Cancel Leave Request"
        confirmButtonText="Cancel Leave" 
        isLoading={isLoading}
        customDescription={ 
          <div className="space-y-4">
            <p className="text-gray-600 text-sm">
              Are you sure you want to cancel your leave request for{" "}
              <span className="font-bold text-gray-800">
                {requestToCancel
                  ? `${requestToCancel.leaveTypeId?.name} on ${formatDateTime(requestToCancel.startDate, false)}`
                  : ""}
              </span>
              ? This action will revert your pending/used balance.
            </p>
            
            {/* ✅ ADDED: Cancellation Reason Textarea Input inside Confirmation dialog */}
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase">Specify Cancellation Reason *</label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="e.g., Plan changed / requested by mistake..."
                className="w-full p-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-1 focus:ring-blue-100"
                rows={3}
                required
              />
            </div>
          </div>
        }
      />
    </div>
  );
};

export default MyLeavesList;